/** Carga masiva del layout de Inscripciones (rescate de #147, 2026-09-28).
 *
 * Bug reportado por Eduardo (2026-07-28): «cuando cargo el layout me sale
 * "División 64 not found", refresco y ya sí me deja; y si ya hay un jugador
 * asignado, no lo mueve». Dos defectos encadenados:
 *
 * 1. Las categorías se resolvían contra la lista EN MEMORIA de la página;
 *    tras editar categorías el id podía ser de una inactiva. Ahora se piden
 *    frescas al cargar y solo cuentan las ACTIVAS.
 * 2. El primer error abortaba el resto de las filas (carga a medias). Ahora
 *    cada fila es independiente y los errores se reportan al final.
 *
 * Además, el cambio de pago por layout usa el PATCH que marca la orden como
 * «manual»: una orden de Mercado Pago/Stripe (pagada o con checkout vivo)
 * perdería su procedencia y el reembolso ya no iría por el proveedor. Esas
 * órdenes NO se tocan desde el layout (se administran desde el panel), y el
 * layout exportado viene pre-llenado: una celda igual al estado actual NO es
 * un cambio.
 */

/** Proveedores con cobro EN LÍNEA: su ledger es del proveedor, no del admin. */
export const ONLINE_PAYMENT_PROVIDERS: ReadonlySet<string> = new Set(["mercadopago_marketplace", "stripe_connect"]);

/** Candado del panel: pago confirmado por el proveedor en línea. */
export function isProviderConfirmedLocked(order: { status: string; provider?: string | null }): boolean {
  return order.status === "paid" && ONLINE_PAYMENT_PROVIDERS.has(order.provider ?? "");
}

/** Categorías contra las que resuelve el layout: estrictamente activas. */
export function activeDivisionsPool<D extends { is_active?: boolean | null }>(divisions: D[]): D[] {
  return divisions.filter((division) => division.is_active === true);
}

export type LayoutPaymentDecision =
  | { kind: "skip" }
  | { kind: "locked" }
  | { kind: "patch"; status: string; notes: string };

/** skip = la fila no trae un cambio real de pago; locked = trae cambio pero
 * la orden es de un proveedor en línea (cero PATCHes, se reporta); patch =
 * aplicar.
 *
 * Notas: el layout exportado trae `notas_pago` PRE-LLENADA con las notas
 * actuales, así que la celda es la nota COMPLETA que el admin quiere (editar
 * la celda no duplica nada). Celda vacía = sin cambio (no se borran notas
 * por layout). `extraNotes` (p. ej. «Comprobante recibido…») se agrega solo
 * si todavía no está. */
export function layoutPaymentDecision(
  order: { status: string; provider?: string | null; notes?: string | null },
  nextStatus: string | null,
  notesCell: string,
  extraNotes: string[] = [],
): LayoutPaymentDecision {
  const existingNotes = (order.notes ?? "").trim();
  let desiredNotes = notesCell.trim() || existingNotes;
  for (const extra of extraNotes) {
    const text = extra.trim();
    if (text && !desiredNotes.includes(text)) desiredNotes = [desiredNotes, text].filter(Boolean).join(" ");
  }
  const statusChanged = nextStatus !== null && nextStatus !== order.status;
  const notesChanged = desiredNotes !== existingNotes;
  if (!statusChanged && !notesChanged) return { kind: "skip" };
  if (ONLINE_PAYMENT_PROVIDERS.has(order.provider ?? "")) return { kind: "locked" };
  return {
    kind: "patch",
    status: statusChanged && nextStatus !== null ? nextStatus : order.status,
    notes: desiredNotes,
  };
}

/** Resumen de las filas que fallaron (primeras 3 + cuántas más). */
export function layoutFailuresMessage(failures: string[]): string | null {
  if (failures.length === 0) return null;
  const extra = failures.length > 3 ? ` · y ${failures.length - 3} más` : "";
  return `Revisa ${failures.length} fila(s) del layout: ${failures.slice(0, 3).join(" · ")}${extra}`;
}
